import { useMemo, FunctionComponent } from "react";
import { IconButton, LinkButton } from "./Button";
import { ArrowLeftIcon, ExternalLinkIcon, PencilIcon } from "./icons";
import {
  iframeUrl,
  linkUrl,
  searchProviders,
} from "@app/utils/searchProviders";
import { usePreferences } from "../contexts/PreferencesContext";
import { AppItem } from "@app/types";

type ResultsPaneProps = {
  item: AppItem;
  /** Dismisses the pane. Only reachable on a phone, where it covers the list. */
  onClose?: () => void;
  /** Opens the edit modal. Absent for a guest, who has nothing to edit. */
  onEdit?: (item: AppItem) => void;
};

/**
 * What a name looks like: a slim header, and the search below it.
 *
 * This was the editor — a strip of name field, tag field, rating and Delete above the
 * results, writing through on every keystroke. Editing moved into the modal that adds
 * an item, and what stays here is the name as text and the two ways out to a real
 * browser tab, which the iframe cannot offer for itself.
 */
export const ResultsPane: FunctionComponent<ResultsPaneProps> = ({
  item,
  onClose,
  onEdit,
}) => {
  const { provider, itemLinkMode } = usePreferences();

  const links = useMemo(() => {
    const engine = searchProviders[provider];
    return {
      all: linkUrl(engine, "standard", item.name),
      hires: linkUrl(engine, "hires", item.name),
      framed: iframeUrl(engine, itemLinkMode, item.name),
    };
  }, [provider, itemLinkMode, item.name]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-line px-2 py-2 md:px-3">
        {/* The way back to the list, and only on a phone: from the `md` breakpoint up
            the list is still on screen beside this. */}
        {onClose ? (
          <IconButton
            label="Back to list"
            onClick={onClose}
            className="md:hidden"
          >
            <ArrowLeftIcon />
          </IconButton>
        ) : null}

        <h2 className="min-w-0 flex-1 truncate px-1 text-sm font-semibold text-ink">
          {item.name}
        </h2>

        {/*
          Editing lives here rather than on the selected row in the sidebar.
          On a phone this pane is `fixed inset-0` over the list, so a pencil in the
          sidebar sat behind it and there was no way to edit anything at all.
        */}
        {onEdit ? (
          <IconButton
            label={`Edit ${item.name}`}
            className="group/edit"
            onClick={() => onEdit(item)}
          >
            <PencilIcon className="size-4 opacity-40 transition-opacity group-hover/edit:opacity-100" />
          </IconButton>
        ) : null}

        <LinkButton href={links.all} target="_blank" rel="noopener noreferrer">
          All
          <ExternalLinkIcon />
        </LinkButton>
        <LinkButton href={links.hires} target="_blank" rel="noopener noreferrer">
          Large
          <ExternalLinkIcon />
        </LinkButton>
      </div>

      {/* `min-h-0` on the column is what lets this take the rest of the height rather
          than pushing the header off the top. */}
      <iframe
        className="min-h-0 flex-1 border-0 bg-surface"
        title={`Search results for ${item.name}`}
        src={links.framed}
        rel="noopener"
      />
    </div>
  );
};
